import { trackError } from '@iheartjane/tracking';
import type { ReservationMode } from '@iheartjane/types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import type { DeepReadonly } from 'ts-essentials';

// eslint-disable-next-line no-restricted-imports
import type { MedRec, Store } from '@jane/shared/models';
import type { UserLocation } from '@jane/shared/types';

import type { ShopByMode } from '../../operatorEmbed/components/operatorEmbedGates';
import { OperatorStoresSource } from '../../operatorEmbed/sources/operatorStoresSource';
import type { CustomerThunkAction } from '../customerAction';
import { setPartner, verifyStore } from './embeddedApp';
import { getStoreError, initiateGetStore } from './store';

export const setStore =
  ({
    operator,
    mode,
    userLocation,
    medOrRec,
    productId,
  }: {
    medOrRec: MedRec;
    mode: ShopByMode;
    operator: { id: string | number };
    productId?: number | null;
    userLocation: UserLocation;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateGetStore());
    const compactedParams = omitBy(
      {
        reservation_mode: mode,
        lat: userLocation?.coordinates?.lat,
        long: userLocation?.coordinates?.long,
        med_rec: medOrRec,
        product_id: productId,
      },
      isNil
    );
    OperatorStoresSource.get(operator.id, compactedParams)
      .then(({ store }: { store: Store }) => {
        if (store) {
          dispatch(
            setPartner({
              id: store.id,
              name: store.name,
            })
          );

          dispatch(
            verifyStore({
              storeId: store.id,
              options: { operatorEmbed: true },
            })
          );
        } else {
          dispatch(getStoreError(null));
        }
      })
      .catch((e) => {
        trackError(e, {
          params: compactedParams,
        });
      });
  };

type OperatorEmbeddedState = DeepReadonly<{
  operatorId: number | string | undefined;
  operatorName: string | undefined;
  reservationMode: ReservationMode | undefined;
}>;

const getInitialState = (): OperatorEmbeddedState => ({
  operatorId: undefined,
  operatorName: undefined,
  reservationMode: undefined,
});

const operatorEmbeddedSlice = createSlice({
  name: 'operatorEmbedded',
  initialState: getInitialState(),
  reducers: {
    setOperator: (
      state,
      action: PayloadAction<{
        id: number | string | undefined;
        name: string | undefined;
      }>
    ) => {
      return {
        ...state,
        operatorId: action.payload.id,
        operatorName: action.payload.name,
      };
    },
    setReservationMode: (
      state,
      action: PayloadAction<ReservationMode | null>
    ) => {
      return { ...state, reservationMode: action.payload || undefined };
    },
  },
  selectors: {
    selectOperatorEmbedded: (state) => state,
  },
});

export const {
  reducer: operatorEmbeddedReducer,
  actions: { setOperator, setReservationMode },
  selectors: { selectOperatorEmbedded },
} = operatorEmbeddedSlice;

export type OperatorEmbeddedActions =
  ReturnType<typeof setOperator> | ReturnType<typeof setReservationMode>;
